/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useContext } from "react";
import EnhancedTable from "components/common_components/Tables/EnhancedTable";
import Page from "components/common_components/Page/Page";
import { useDispatch } from "react-redux";
import { notifySuccess, notifyError, notifyInfo } from "redux/actions/Notifications";
import LoadingPage from "components/common_components/LoadingPage";
import Icon from "components/common_components/Icon";
import Button from "components/common_components/Button";
import SettingsContext from "components/Settings/SettingsContext";
import AppContext from "AppContext";
import Status from "components/common_components/Status";
import { ConfirmDialog } from "components/common_components/Dialogs";
import AddForm from "./AddForm";
import FormRowActions from "./FormRowActions";
import { Grid, MenuItem, TextField } from "@material-ui/core";
import SearchInput from "components/common_components/SearchInput/SearchInput";

function Forms() {
    const dispatch = useDispatch();
    const [isLoading, setIsLoading] = useState(false);
    const [data, setData] = useState([]);
    const [resources, setResources] = useState({ issuers: [], categories: [] });
    const [mode, setMode] = useState("listOnly");
    const [itemToEdit, setItemToEdit] = useState(undefined);
    const [pageableData, setPageableData] = useState(undefined);
    const [status, setStatus] = useState("active");
    const [quickFilterValue, setQuickFilterValue] = useState("");

    const onQuickFilterChange = (e) => {
        setQuickFilterValue(e.target.value);
    };

    const closeConfirmDialog = () => setConfirmDialogProps((prev) => ({ ...prev, open: false }));
    const [confirmDialogProps, setConfirmDialogProps] = useState({
        open: false,
        title: "",
        content: "",
        primaryButtonText: "Rollback",
        onPrimaryClick: undefined,
        secondaryButtonText: "Cancel",
        onSecondaryClick: closeConfirmDialog,
        onClose: closeConfirmDialog,
    });

    const {
        Forms: { Forms, Issuers, Categories },
    } = useContext(SettingsContext);

    const {
        api: {
            General: { Storage },
        },
    } = useContext(AppContext);

    function getForms(requestData, showLoader = true) {
        if (showLoader) {
            setIsLoading(true);
        }
        Forms.getList({
            onSuccess: (data, pageableData) => {
                setData(data);
                setPageableData(pageableData);
                setIsLoading(false);
            },
            onInfo: (info, pageableData) => {
                dispatch(notifyInfo(info));
                setPageableData(pageableData);
                setIsLoading(false);
            },
            onError: (error) => {
                dispatch(notifyError(error));
                setIsLoading(false);
            },
            data: requestData,
        });
    }

    function getIssuers() {
        setIsLoading(true);
        Issuers.getAll({
            onSuccess: (data) => {
                setResources((prev) => ({
                    ...prev,
                    issuers: data,
                }));
                setIsLoading(false);
            },
            onError: (error) => {
                setResources((prev) => ({
                    ...prev,
                    issuers: [],
                }));
                dispatch(notifyError(error));
                setIsLoading(false);
            },
        });
    }

    function getCategories() {
        setIsLoading(true);
        Categories.getAll({
            onSuccess: (data) => {
                setResources((prev) => ({
                    ...prev,
                    categories: data,
                }));
                setIsLoading(false);
            },
            onError: (error) => {
                setResources((prev) => ({
                    ...prev,
                    categories: [],
                }));
                dispatch(notifyError(error));
                setIsLoading(false);
            },
        });
    }

    useEffect(() => {
        getIssuers();
        getCategories();

        return () => {
            setData([]);
            setItemToEdit(undefined);
        };
    }, []);

    useEffect(() => {
        onFilterChange(quickFilterValue);
    }, [status]);

    function onRowClickHandler(item) {
        setItemToEdit(item);
        setMode("update");
    }

    function onCancelHandler() {
        setItemToEdit(undefined);
        setMode("listOnly");
    }

    function onSuccess(_, setSubmitting) {
        setSubmitting(false);
        onCancelHandler();
        dispatch(notifySuccess(`Form ${mode === "add" ? "created" : "updated"} successfully!`));
        onFilterChange(quickFilterValue);
    }

    function onError(error, setSubmitting, setErrors) {
        if (error && error.errors) {
            setErrors(error.errors);
            dispatch(notifyError(error.message));
        } else {
            onCancelHandler();
            dispatch(notifyError(error));
        }
        setSubmitting(false);
    }

    function onSubmitHandler(values, { setSubmitting, setErrors }) {
        const { id, issuer, category, code, version, expires, extraInfo, officialUrl, isCurrent, fileUrl, status, formFieldMappings } =
            values;

        const apiCall = mode === "add" ? Forms.add : Forms.update;

        apiCall({
            payload: {
                id,
                issuer,
                category,
                code,
                version,
                expires,
                extraInfo,
                officialUrl,
                isCurrent,
                status,
                formFieldMappings,
                fileUrl,
            },
            onSuccess: (response) => onSuccess(response, setSubmitting),
            onError: (error) => onError(error, setSubmitting, setErrors),
        });
    }

    function onDeleteSuccess() {
        dispatch(notifyInfo("Removed successfully!"));
        onFilterChange(quickFilterValue);
    }

    function onDeleteError(error) {
        dispatch(notifyError(error.message));
    }

    function onDeleteHandler(selectedItems) {
        Forms.deleteAll({
            ids: selectedItems,
            onSuccess: onDeleteSuccess,
            onError: onDeleteError,
        });
    }

    function rollback(form) {
        closeConfirmDialog();
        setIsLoading(true);
        Forms.rollback({
            id: form.id,
            onSuccess: async () => {
                // The rolled back version no longer exists, so its PDF is removed too
                try {
                    await Storage.deleteAsync({
                        path: `settings/forms/pdf-files/${form.issuer.id}/${form.category.id}/${form.code}_${form.version}.pdf`,
                    });
                } catch (error) {}
                dispatch(notifySuccess(`Version ${form.version} of ${form.code} was rolled back successfully!`));
                onFilterChange(quickFilterValue);
            },
            onError: (error) => {
                dispatch(notifyError(error.message || error));
                setIsLoading(false);
            },
        });
    }

    function onAddVersionHandler(form) {
        setItemToEdit({
            ...form,
            id: null,
            expires: null,
            officialUrl: form.officialUrl || "",
            fileUrl: null,
            formFieldMappings: [],
            isCurrent: true,
            status: "active",
        });
        setMode("add");
    }

    function onRollbackHandler(form) {
        setConfirmDialogProps((prev) => ({
            ...prev,
            open: true,
            title: `Rollback ${form.code} version ${form.version}?`,
            content:
                "This version, its field mappings and its PDF file will be permanently removed, and the previous version will become the current one again. This is only allowed if no client form uses this version yet.",
            onPrimaryClick: () => rollback(form),
        }));
    }

    function buildCustomFilter(value) {
        const customFilters = {};
        if (value) {
            customFilters.code = value;
            customFilters.version = value;
        }
        if (status) {
            customFilters.status = status;
        }
        return customFilters;
    }

    function onAddHandler() {
        setItemToEdit(undefined);
        setMode("add");
    }

    const headCells = [
        { index: 1, id: "issuer.issuer", label: "Issuer" },
        { index: 2, id: "category.category", label: "Category" },
        { index: 3, id: "code", label: "Code" },
        { index: 4, id: "version", label: "Version" },
        { index: 5, id: "expires", label: "Expires", type: "date" },
        { index: 6, id: "isCurrent", label: "Current", type: "bool" },
        {
            index: 7,
            id: "status",
            label: "Status",
            render: (_, { value }) => {
                return <Status id={value} withIcon />;
            },
            sortable: false,
        },
        {
            index: 8,
            id: "actions",
            label: "",
            render: (_, { data }) => (
                <FormRowActions form={data} onAddVersion={onAddVersionHandler} onRollback={onRollbackHandler} />
            ),
            sortable: false,
        },
    ];

    const onStatusChange = (e) => {
        setStatus(e.target.value);
    };

    const onFilterChange = (value) => {
        getForms({
            filter: buildCustomFilter(value),
            orderBy: "id",
            orderDirection: "desc",
            pageNumber: 0,
            pageSize: 15,
        });
    };

    return (
        <Page className="settings-forms-page">
            {isLoading ? (
                <LoadingPage />
            ) : (
                <>
                    {mode !== "listOnly" && (
                        <AddForm
                            data={itemToEdit}
                            resources={resources}
                            mode={mode}
                            onSubmit={onSubmitHandler}
                            onCancel={onCancelHandler}
                            title={mode === "add" ? "New Form" : "Update Form"}
                        />
                    )}
                    {mode === "listOnly" && (
                        <>
                            <Button primary onClick={onAddHandler}>
                                <Icon name="add" size="md" color="white" />
                                <span>Add</span>
                            </Button>
                            <div className="layout custom-filters">
                                <Grid container spacing={2} alignItems="flex-end">
                                    <Grid item xs={12} sm={6} md={2}>
                                        <TextField
                                            select
                                            value={status}
                                            onChange={onStatusChange}
                                            label="Status"
                                            multiline
                                            fullWidth
                                            size="small"
                                        >
                                            {["active", "inactive"].map((s, index) => (
                                                <MenuItem key={index} value={s}>
                                                    <Status id={s} withIcon />
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={3}>
                                        <SearchInput
                                            value={quickFilterValue}
                                            onChange={onQuickFilterChange}
                                            onSearch={onFilterChange}
                                            showLabel
                                        />
                                    </Grid>
                                </Grid>
                            </div>
                            <EnhancedTable
                                headCells={headCells}
                                data={data}
                                defaultOrder={{
                                    order: "desc",
                                    orderBy: "id",
                                }}
                                selectable
                                keyColumnName="id"
                                onRowClick={onRowClickHandler}
                                onDelete={onDeleteHandler}
                                onRequestData={getForms}
                                pageableData={pageableData}
                                defaultRowsPerPage={15}
                                filterable={false}
                                filterBuilder={() => buildCustomFilter(quickFilterValue)}
                            />
                        </>
                    )}
                </>
            )}
            <ConfirmDialog {...confirmDialogProps} />
        </Page>
    );
}

export default Forms;
